import type { ProcessedComboProduct, ProductDetailVariant } from '../types/api';

interface ComboProductSelectorsProps {
  comboProducts: ProcessedComboProduct[];
  selectedComboVariants: Record<number, ProductDetailVariant>;
  onComboVariantChange: (comboProductId: number, optionName: string, optionValue: string | number) => void;
}

/**
 * ComboProductSelectors — renders labeled dropdown selectors for each visible
 * combo product option. Hidden options (auto-matched via auto-hide logic) are
 * not rendered; they are pre-selected silently via combo_product_default_value.
 *
 * Styling matches ProductOptionSelectors from ProductDetailOverlay (SPEC-035 pattern).
 * Supports SPEC-036: PDP Combo Products (AC-12, AC-13, AC-14, AC-15).
 */
export function ComboProductSelectors({
  comboProducts,
  selectedComboVariants,
  onComboVariantChange,
}: ComboProductSelectorsProps) {
  if (comboProducts.length === 0) return null;

  return (
    <div className="space-y-4">
      {comboProducts.map((combo) => {
        const visibleOptions = combo.comboVariantOptions.filter(
          (opt) => !opt.combo_product_hidden
        );

        // If all options are hidden, nothing to render for this combo product
        if (visibleOptions.length === 0) return null;

        return (
          <div key={combo.product.id}>
            {visibleOptions.map((option) => {
              // Get currently selected variant for this combo product
              const selectedVariant = selectedComboVariants[combo.product.id];

              // Determine the currently selected value for this option from the selected variant
              const selectedValue =
                selectedVariant?.options.find((o) => o.name === option.name)?.value ??
                option.value;

              // Available values: exclude disabled options (ignoreDisabledOptions matches Angular behavior)
              const availableValues = option.available ?? [];
              const enabledValues = availableValues.filter((a) => !a.disabled);
              // If all values are disabled, show all (graceful fallback)
              const displayValues = enabledValues.length > 0 ? enabledValues : availableValues;

              return (
                <div key={option.name}>
                  {/* AC-14: display label from combo_configuration */}
                  {option.combo_product_label && (
                    <p className="text-xs uppercase tracking-[0.15em] text-brand-navy/60 mb-2">
                      {option.combo_product_label}
                    </p>
                  )}
                  {/* AC-12: labeled dropdown for visible combo option */}
                  <select
                    value={String(selectedValue)}
                    onChange={(e) =>
                      onComboVariantChange(
                        combo.product.id,
                        option.name,
                        e.target.value
                      )
                    }
                    className="w-full border border-brand-gold/30 rounded-sm px-3 py-2 text-sm text-brand-navy bg-transparent focus:outline-none focus:border-brand-navy transition-colors"
                  >
                    {/* AC-15: dropdown options show all available variant values */}
                    {displayValues.map((avail) => (
                      <option key={String(avail.value)} value={String(avail.value)}>
                        {String(avail.value)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
